// Healthcare Personas Library
// This file contains persona definitions for call simulation

export interface Persona {
  id: string
  name: string
  category: string
  type: string
  intent: string
  language: string
  emotionalState: string
  behaviorTraits: string
  riskFlags: string
  riskLevel: string
  location?: string
  systemPrompt?: string
}

export const defaultPersonas: Persona[] = [
  // Category 1: Demographic Personas (10)
  {
    id: "demo-1",
    name: "Senior Citizen - Confused",
    category: "Demographic",
    type: "Default",
    intent: "Appointment Booking",
    language: "English (US)",
    emotionalState: "Confused, Anxious",
    behaviorTraits: "Slow speaker, Repeats questions",
    riskFlags: "Requires patience",
    riskLevel: "Medium",
  },
  {
    id: "demo-2",
    name: "Young Parent - Urgent",
    category: "Demographic",
    type: "Default",
    intent: "Emergency Inquiry",
    language: "English (US)",
    emotionalState: "Worried, Rushed",
    behaviorTraits: "Fast speaker, Background noise (children)",
    riskFlags: "May need quick response",
    riskLevel: "High",
  },
  {
    id: "demo-3",
    name: "Middle-Aged Professional",
    category: "Demographic",
    type: "Default",
    intent: "Test Results Inquiry",
    language: "English (US)",
    emotionalState: "Neutral, Efficient",
    behaviorTraits: "Direct, Time-conscious",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "demo-4",
    name: "Elderly with Hearing Issues",
    category: "Demographic",
    type: "Default",
    intent: "Prescription Refill",
    language: "English (US)",
    emotionalState: "Patient, Struggling",
    behaviorTraits: "Asks for repetition, Speaks loudly",
    riskFlags: "Communication barrier",
    riskLevel: "Medium",
  },
  {
    id: "demo-5",
    name: "College Student",
    category: "Demographic",
    type: "Default",
    intent: "Billing Question",
    language: "English (US)",
    emotionalState: "Casual, Informal",
    behaviorTraits: "Uses slang, Brief responses",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "demo-6",
    name: "Retired Veteran",
    category: "Demographic",
    type: "Default",
    intent: "Specialist Referral",
    language: "English (US)",
    emotionalState: "Formal, Respectful",
    behaviorTraits: "Structured communication",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "demo-7",
    name: "New Mother",
    category: "Demographic",
    type: "Default",
    intent: "Pediatric Appointment",
    language: "English (US)",
    emotionalState: "Concerned, Detailed",
    behaviorTraits: "Asks many questions",
    riskFlags: "Child safety priority",
    riskLevel: "Medium",
  },
  {
    id: "demo-8",
    name: "Tech-Savvy Millennial",
    category: "Demographic",
    type: "Default",
    intent: "Telehealth Setup",
    language: "English (US)",
    emotionalState: "Confident, Independent",
    behaviorTraits: "Self-service oriented",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "demo-9",
    name: "Caregiver for Elderly Parent",
    category: "Demographic",
    type: "Default",
    intent: "Care Coordination",
    language: "English (US)",
    emotionalState: "Stressed, Multitasking",
    behaviorTraits: "Juggling responsibilities",
    riskFlags: "May be overwhelmed",
    riskLevel: "Medium",
  },
  {
    id: "demo-10",
    name: "Pregnant Patient - First Trimester",
    category: "Demographic",
    type: "Default",
    intent: "Prenatal Appointment",
    language: "English (US)",
    emotionalState: "Excited, Nervous",
    behaviorTraits: "Seeking reassurance",
    riskFlags: "Pregnancy-related concerns",
    riskLevel: "Medium",
  },

  // Category 2: Language Personas (10)
  {
    id: "lang-1",
    name: "Spanish Speaker - Limited English",
    category: "Language",
    type: "Default",
    intent: "Appointment Booking",
    language: "Spanish",
    emotionalState: "Hesitant, Apologetic",
    behaviorTraits: "Language barrier, Slow communication",
    riskFlags: "Requires language support",
    riskLevel: "High",
  },
  {
    id: "lang-2",
    name: "Mandarin Speaker",
    category: "Language",
    type: "Default",
    intent: "Billing Inquiry",
    language: "Mandarin Chinese",
    emotionalState: "Neutral",
    behaviorTraits: "Prefers written confirmation",
    riskFlags: "Language support needed",
    riskLevel: "High",
  },
  {
    id: "lang-3",
    name: "Hindi Speaker - Tech Worker",
    category: "Language",
    type: "Default",
    intent: "Test Results",
    language: "Hindi / English",
    emotionalState: "Professional",
    behaviorTraits: "Code-switches languages",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "lang-4",
    name: "Vietnamese Elderly Patient",
    category: "Language",
    type: "Default",
    intent: "Follow-up Appointment",
    language: "Vietnamese",
    emotionalState: "Polite, Deferential",
    behaviorTraits: "Cultural communication norms",
    riskFlags: "Language and cultural barriers",
    riskLevel: "High",
  },
  {
    id: "lang-5",
    name: "French Canadian",
    category: "Language",
    type: "Default",
    intent: "Insurance Verification",
    language: "French",
    emotionalState: "Neutral",
    behaviorTraits: "Direct communication",
    riskFlags: "Language support needed",
    riskLevel: "Medium",
  },
  {
    id: "lang-6",
    name: "Korean Speaker - Limited English",
    category: "Language",
    type: "Default",
    intent: "Prescription Question",
    language: "Korean",
    emotionalState: "Confused",
    behaviorTraits: "Needs translation assistance",
    riskFlags: "Critical language barrier",
    riskLevel: "High",
  },
  {
    id: "lang-7",
    name: "Arabic Speaker",
    category: "Language",
    type: "Default",
    intent: "New Patient Registration",
    language: "Arabic",
    emotionalState: "Formal",
    behaviorTraits: "Cultural communication preferences",
    riskFlags: "Language and cultural considerations",
    riskLevel: "High",
  },
  {
    id: "lang-8",
    name: "Tagalog Speaker - Healthcare Worker",
    category: "Language",
    type: "Default",
    intent: "Personal Appointment",
    language: "Tagalog / English",
    emotionalState: "Efficient",
    behaviorTraits: "Medical knowledge, Bilingual",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "lang-9",
    name: "Russian Speaker - Senior",
    category: "Language",
    type: "Default",
    intent: "Medication Refill",
    language: "Russian",
    emotionalState: "Patient",
    behaviorTraits: "Heavy accent, Slow speech",
    riskFlags: "Language barrier",
    riskLevel: "High",
  },
  {
    id: "lang-10",
    name: "Portuguese Speaker - Brazilian",
    category: "Language",
    type: "Default",
    intent: "Specialist Referral",
    language: "Portuguese",
    emotionalState: "Friendly, Warm",
    behaviorTraits: "Expressive communication",
    riskFlags: "Language support preferred",
    riskLevel: "Medium",
  },

  // Category 3: Emotional State Personas (10)
  {
    id: "emot-1",
    name: "Anxious Patient - Health Concerns",
    category: "Emotional State",
    type: "Default",
    intent: "Symptom Discussion",
    language: "English (US)",
    emotionalState: "Highly Anxious, Worried",
    behaviorTraits: "Seeks constant reassurance",
    riskFlags: "May catastrophize",
    riskLevel: "Medium",
  },
  {
    id: "emot-2",
    name: "Depressed Patient",
    category: "Emotional State",
    type: "Default",
    intent: "Mental Health Appointment",
    language: "English (US)",
    emotionalState: "Low energy, Withdrawn",
    behaviorTraits: "Short responses, Lacks motivation",
    riskFlags: "Mental health concern",
    riskLevel: "High",
  },
  {
    id: "emot-3",
    name: "Angry Billing Dispute",
    category: "Emotional State",
    type: "Stress",
    intent: "Billing Complaint",
    language: "English (US)",
    emotionalState: "Angry, Frustrated",
    behaviorTraits: "Raises voice, Interrupts",
    riskFlags: "High escalation risk",
    riskLevel: "High",
  },
  {
    id: "emot-4",
    name: "Relieved Test Results",
    category: "Emotional State",
    type: "Default",
    intent: "Follow-up Discussion",
    language: "English (US)",
    emotionalState: "Relieved, Grateful",
    behaviorTraits: "Positive, Cooperative",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "emot-5",
    name: "Fearful Surgery Patient",
    category: "Emotional State",
    type: "Default",
    intent: "Pre-surgery Questions",
    language: "English (US)",
    emotionalState: "Fearful, Nervous",
    behaviorTraits: "Needs emotional support",
    riskFlags: "Requires empathy",
    riskLevel: "Medium",
  },
  {
    id: "emot-6",
    name: "Overwhelmed Cancer Diagnosis",
    category: "Emotional State",
    type: "Stress",
    intent: "Treatment Planning",
    language: "English (US)",
    emotionalState: "Shocked, Overwhelmed",
    behaviorTraits: "Difficulty processing information",
    riskFlags: "Critical emotional state",
    riskLevel: "High",
  },
  {
    id: "emot-7",
    name: "Impatient Wait Time",
    category: "Emotional State",
    type: "Stress",
    intent: "Appointment Status",
    language: "English (US)",
    emotionalState: "Impatient, Irritated",
    behaviorTraits: "Short temper, Demands immediate action",
    riskFlags: "Escalation potential",
    riskLevel: "Medium",
  },
  {
    id: "emot-8",
    name: "Grateful Post-treatment",
    category: "Emotional State",
    type: "Default",
    intent: "Thank You Call",
    language: "English (US)",
    emotionalState: "Happy, Appreciative",
    behaviorTraits: "Positive feedback",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "emot-9",
    name: "Confused Medication Instructions",
    category: "Emotional State",
    type: "Default",
    intent: "Prescription Clarification",
    language: "English (US)",
    emotionalState: "Confused, Uncertain",
    behaviorTraits: "Needs clear explanation",
    riskFlags: "Medication safety concern",
    riskLevel: "Medium",
  },
  {
    id: "emot-10",
    name: "Grief - Recent Loss",
    category: "Emotional State",
    type: "Stress",
    intent: "Bereavement Support",
    language: "English (US)",
    emotionalState: "Grieving, Sad",
    behaviorTraits: "Emotional, May cry",
    riskFlags: "Requires compassion",
    riskLevel: "High",
  },

  // Category 4: Intent-Based Personas (10)
  {
    id: "intent-1",
    name: "Appointment Scheduler - Routine",
    category: "Intent-Based",
    type: "Default",
    intent: "Schedule Appointment",
    language: "English (US)",
    emotionalState: "Neutral",
    behaviorTraits: "Straightforward, Efficient",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "intent-2",
    name: "Emergency Caller",
    category: "Intent-Based",
    type: "Stress",
    intent: "Emergency Medical Situation",
    language: "English (US)",
    emotionalState: "Panicked, Urgent",
    behaviorTraits: "Fast speech, Distressed",
    riskFlags: "Critical emergency",
    riskLevel: "High",
  },
  {
    id: "intent-3",
    name: "Prescription Refill - Routine",
    category: "Intent-Based",
    type: "Default",
    intent: "Medication Refill",
    language: "English (US)",
    emotionalState: "Neutral",
    behaviorTraits: "Knows process, Quick",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "intent-4",
    name: "Test Result Inquiry - Anxious",
    category: "Intent-Based",
    type: "Default",
    intent: "Lab Results",
    language: "English (US)",
    emotionalState: "Anxious, Anticipating",
    behaviorTraits: "Nervous, Wants immediate answers",
    riskFlags: "May receive bad news",
    riskLevel: "Medium",
  },
  {
    id: "intent-5",
    name: "Insurance Verification",
    category: "Intent-Based",
    type: "Default",
    intent: "Coverage Check",
    language: "English (US)",
    emotionalState: "Neutral, Business-like",
    behaviorTraits: "Detail-oriented",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "intent-6",
    name: "Billing Dispute - Frustrated",
    category: "Intent-Based",
    type: "Stress",
    intent: "Billing Issue Resolution",
    language: "English (US)",
    emotionalState: "Frustrated, Skeptical",
    behaviorTraits: "Demands explanation",
    riskFlags: "Potential complaint",
    riskLevel: "Medium",
  },
  {
    id: "intent-7",
    name: "Second Opinion Seeker",
    category: "Intent-Based",
    type: "Default",
    intent: "Consultation Request",
    language: "English (US)",
    emotionalState: "Cautious, Analytical",
    behaviorTraits: "Asks detailed questions",
    riskFlags: "May distrust initial diagnosis",
    riskLevel: "Low",
  },
  {
    id: "intent-8",
    name: "Telehealth Technical Support",
    category: "Intent-Based",
    type: "Default",
    intent: "Technical Assistance",
    language: "English (US)",
    emotionalState: "Frustrated with technology",
    behaviorTraits: "Not tech-savvy",
    riskFlags: "Digital divide",
    riskLevel: "Medium",
  },
  {
    id: "intent-9",
    name: "Medical Records Request",
    category: "Intent-Based",
    type: "Default",
    intent: "Records Transfer",
    language: "English (US)",
    emotionalState: "Neutral, Administrative",
    behaviorTraits: "Formal, Process-oriented",
    riskFlags: "HIPAA compliance required",
    riskLevel: "Medium",
  },
  {
    id: "intent-10",
    name: "Complaint Filing",
    category: "Intent-Based",
    type: "Stress",
    intent: "Formal Complaint",
    language: "English (US)",
    emotionalState: "Angry, Dissatisfied",
    behaviorTraits: "Demands escalation",
    riskFlags: "Potential legal issue",
    riskLevel: "High",
  },

  // Category 5: Medical Context Personas (10)
  {
    id: "med-1",
    name: "Chronic Pain Patient",
    category: "Medical Context",
    type: "Default",
    intent: "Pain Management",
    language: "English (US)",
    emotionalState: "Suffering, Desperate",
    behaviorTraits: "Seeks relief, May be on opioids",
    riskFlags: "Controlled substance considerations",
    riskLevel: "High",
  },
  {
    id: "med-2",
    name: "Diabetic - Regular Monitoring",
    category: "Medical Context",
    type: "Default",
    intent: "Routine Check-in",
    language: "English (US)",
    emotionalState: "Neutral, Compliant",
    behaviorTraits: "Understands condition well",
    riskFlags: "Chronic disease management",
    riskLevel: "Medium",
  },
  {
    id: "med-3",
    name: "Heart Attack Survivor",
    category: "Medical Context",
    type: "Default",
    intent: "Cardiac Rehabilitation",
    language: "English (US)",
    emotionalState: "Cautious, Health-conscious",
    behaviorTraits: "Follows instructions carefully",
    riskFlags: "Critical health history",
    riskLevel: "High",
  },
  {
    id: "med-4",
    name: "Pregnant - High Risk",
    category: "Medical Context",
    type: "Default",
    intent: "Prenatal Care",
    language: "English (US)",
    emotionalState: "Anxious, Protective",
    behaviorTraits: "Frequent questions, Worried",
    riskFlags: "High-risk pregnancy",
    riskLevel: "High",
  },
  {
    id: "med-5",
    name: "Mental Health - Bipolar",
    category: "Medical Context",
    type: "Default",
    intent: "Medication Management",
    language: "English (US)",
    emotionalState: "Variable mood",
    behaviorTraits: "Mood-dependent behavior",
    riskFlags: "Mental health monitoring",
    riskLevel: "High",
  },
  {
    id: "med-6",
    name: "Cancer Patient - Active Treatment",
    category: "Medical Context",
    type: "Default",
    intent: "Chemotherapy Scheduling",
    language: "English (US)",
    emotionalState: "Resilient but tired",
    behaviorTraits: "Experienced with medical system",
    riskFlags: "Serious illness",
    riskLevel: "High",
  },
  {
    id: "med-7",
    name: "Asthma - Acute Exacerbation",
    category: "Medical Context",
    type: "Stress",
    intent: "Urgent Refill",
    language: "English (US)",
    emotionalState: "Breathless, Worried",
    behaviorTraits: "Difficulty speaking",
    riskFlags: "Respiratory emergency potential",
    riskLevel: "High",
  },
  {
    id: "med-8",
    name: "Alzheimer's Family Caregiver",
    category: "Medical Context",
    type: "Default",
    intent: "Care Coordination",
    language: "English (US)",
    emotionalState: "Exhausted, Stressed",
    behaviorTraits: "Managing complex care needs",
    riskFlags: "Caregiver burnout",
    riskLevel: "Medium",
  },
  {
    id: "med-9",
    name: "Post-Surgery Recovery",
    category: "Medical Context",
    type: "Default",
    intent: "Follow-up Care",
    language: "English (US)",
    emotionalState: "Recovering, Hopeful",
    behaviorTraits: "Needs clear post-op instructions",
    riskFlags: "Surgical complications watch",
    riskLevel: "Medium",
  },
  {
    id: "med-10",
    name: "Rare Disease Patient",
    category: "Medical Context",
    type: "Default",
    intent: "Specialist Consultation",
    language: "English (US)",
    emotionalState: "Knowledgeable, Advocacy-focused",
    behaviorTraits: "Well-researched, Proactive",
    riskFlags: "Complex medical needs",
    riskLevel: "High",
  },

  // Category 6: Stress & Aggression Personas (10)
  {
    id: "stress-1",
    name: "Verbally Abusive Caller",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Complaint",
    language: "English (US)",
    emotionalState: "Hostile, Aggressive",
    behaviorTraits: "Uses profanity, Threatens",
    riskFlags: "Severe escalation risk",
    riskLevel: "High",
  },
  {
    id: "stress-2",
    name: "Demanding Immediate Service",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Priority Service Request",
    language: "English (US)",
    emotionalState: "Entitled, Impatient",
    behaviorTraits: "Refuses to wait, Name-drops",
    riskFlags: "Difficult interaction",
    riskLevel: "Medium",
  },
  {
    id: "stress-3",
    name: "Litigation Threat",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Legal Complaint",
    language: "English (US)",
    emotionalState: "Angry, Litigious",
    behaviorTraits: "Mentions lawyer, Records call",
    riskFlags: "Legal risk",
    riskLevel: "High",
  },
  {
    id: "stress-4",
    name: "Intoxicated Caller",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Unclear",
    language: "English (US)",
    emotionalState: "Impaired, Belligerent",
    behaviorTraits: "Slurred speech, Incoherent",
    riskFlags: "Safety concern",
    riskLevel: "High",
  },
  {
    id: "stress-5",
    name: "Paranoid Conspiracy Theorist",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Medical Mistrust",
    language: "English (US)",
    emotionalState: "Suspicious, Confrontational",
    behaviorTraits: "Rejects standard medical advice",
    riskFlags: "Non-compliance risk",
    riskLevel: "Medium",
  },
  {
    id: "stress-6",
    name: "Screaming Emergency",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Crisis Intervention",
    language: "English (US)",
    emotionalState: "Hysterical, Panicked",
    behaviorTraits: "Yelling, Incoherent",
    riskFlags: "Emergency situation",
    riskLevel: "High",
  },
  {
    id: "stress-7",
    name: "Passive-Aggressive Complaint",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Indirect Criticism",
    language: "English (US)",
    emotionalState: "Resentful, Sarcastic",
    behaviorTraits: "Veiled insults, Backhanded compliments",
    riskFlags: "Difficult communication",
    riskLevel: "Medium",
  },
  {
    id: "stress-8",
    name: "Road Rage Transfer",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Displaced Anger",
    language: "English (US)",
    emotionalState: "Enraged, Unreasonable",
    behaviorTraits: "Takes out frustration on agent",
    riskFlags: "Irrational behavior",
    riskLevel: "High",
  },
  {
    id: "stress-9",
    name: "Repeated Complainer",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Chronic Dissatisfaction",
    language: "English (US)",
    emotionalState: "Bitter, Cynical",
    behaviorTraits: "Nothing is ever good enough",
    riskFlags: "Unreasonable expectations",
    riskLevel: "Medium",
  },
  {
    id: "stress-10",
    name: "Substance Abuse - Drug Seeking",
    category: "Stress & Aggression",
    type: "Stress",
    intent: "Controlled Substance Request",
    language: "English (US)",
    emotionalState: "Desperate, Manipulative",
    behaviorTraits: "Doctor shopping, Lies about symptoms",
    riskFlags: "Drug-seeking behavior",
    riskLevel: "High",
  },

  // Category 7: Compliance & Safety Personas (10)
  {
    id: "comp-1",
    name: "Suicide Risk Caller",
    category: "Compliance & Safety",
    type: "Stress",
    intent: "Mental Health Crisis",
    language: "English (US)",
    emotionalState: "Hopeless, Suicidal",
    behaviorTraits: "Expresses intent to harm self",
    riskFlags: "Immediate safety risk",
    riskLevel: "High",
  },
  {
    id: "comp-2",
    name: "Domestic Violence Victim",
    category: "Compliance & Safety",
    type: "Stress",
    intent: "Seeking Help (Covertly)",
    language: "English (US)",
    emotionalState: "Fearful, Guarded",
    behaviorTraits: "Speaks in code, Whispers",
    riskFlags: "Safety and mandatory reporting",
    riskLevel: "High",
  },
  {
    id: "comp-3",
    name: "Child Abuse Disclosure",
    category: "Compliance & Safety",
    type: "Stress",
    intent: "Reporting Concern",
    language: "English (US)",
    emotionalState: "Distressed, Protective",
    behaviorTraits: "Describes signs of abuse",
    riskFlags: "Mandatory reporting required",
    riskLevel: "High",
  },
  {
    id: "comp-4",
    name: "HIPAA Privacy Request",
    category: "Compliance & Safety",
    type: "Default",
    intent: "Privacy Rights",
    language: "English (US)",
    emotionalState: "Assertive, Informed",
    behaviorTraits: "Knows patient rights",
    riskFlags: "Compliance verification needed",
    riskLevel: "Medium",
  },
  {
    id: "comp-5",
    name: "Medicare Fraud Concern",
    category: "Compliance & Safety",
    type: "Default",
    intent: "Fraud Reporting",
    language: "English (US)",
    emotionalState: "Suspicious, Cautious",
    behaviorTraits: "Questions billing practices",
    riskFlags: "Compliance investigation potential",
    riskLevel: "High",
  },
  {
    id: "comp-6",
    name: "Infectious Disease Exposure",
    category: "Compliance & Safety",
    type: "Stress",
    intent: "Public Health Reporting",
    language: "English (US)",
    emotionalState: "Worried, Responsible",
    behaviorTraits: "Wants to follow protocol",
    riskFlags: "Public health reporting required",
    riskLevel: "High",
  },
  {
    id: "comp-7",
    name: "Medication Error Victim",
    category: "Compliance & Safety",
    type: "Stress",
    intent: "Adverse Event Reporting",
    language: "English (US)",
    emotionalState: "Upset, Concerned",
    behaviorTraits: "Describes error consequences",
    riskFlags: "Patient safety incident",
    riskLevel: "High",
  },
  {
    id: "comp-8",
    name: "Informed Consent Question",
    category: "Compliance & Safety",
    type: "Default",
    intent: "Legal Rights Inquiry",
    language: "English (US)",
    emotionalState: "Cautious, Analytical",
    behaviorTraits: "Asks about consent process",
    riskFlags: "Legal compliance verification",
    riskLevel: "Medium",
  },
  {
    id: "comp-9",
    name: "Elder Abuse Suspicion",
    category: "Compliance & Safety",
    type: "Stress",
    intent: "Protective Services",
    language: "English (US)",
    emotionalState: "Concerned, Uncertain",
    behaviorTraits: "Reports signs of neglect",
    riskFlags: "Mandatory reporting trigger",
    riskLevel: "High",
  },
  {
    id: "comp-10",
    name: "Data Breach Inquiry",
    category: "Compliance & Safety",
    type: "Stress",
    intent: "Security Concern",
    language: "English (US)",
    emotionalState: "Alarmed, Demanding",
    behaviorTraits: "Questions data protection",
    riskFlags: "HIPAA breach protocol",
    riskLevel: "High",
  },

  // Category 8: Rural & Low-Literacy Personas (10)
  {
    id: "rural-1",
    name: "Rural Farmer - Limited Tech",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Basic Appointment",
    language: "English (US - Rural dialect)",
    emotionalState: "Humble, Simple",
    behaviorTraits: "Unfamiliar with medical terminology",
    riskFlags: "Communication barrier",
    riskLevel: "Medium",
  },
  {
    id: "rural-2",
    name: "Low Health Literacy",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Medication Instructions",
    language: "English (US)",
    emotionalState: "Confused, Embarrassed",
    behaviorTraits: "Cannot read prescription labels",
    riskFlags: "Medication safety risk",
    riskLevel: "High",
  },
  {
    id: "rural-3",
    name: "Elderly Rural Resident",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Transportation Assistance",
    language: "English (US - Regional)",
    emotionalState: "Isolated, Grateful",
    behaviorTraits: "Limited access to services",
    riskFlags: "Social determinants of health",
    riskLevel: "Medium",
  },
  {
    id: "rural-4",
    name: "Immigrant Farm Worker",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Occupational Injury",
    language: "Spanish - Limited English",
    emotionalState: "Hesitant, Fearful",
    behaviorTraits: "Fears deportation, Lacks insurance",
    riskFlags: "Vulnerable population",
    riskLevel: "High",
  },
  {
    id: "rural-5",
    name: "Appalachian Coal Miner",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Black Lung Screening",
    language: "English (US - Appalachian)",
    emotionalState: "Stoic, Resigned",
    behaviorTraits: "Distrusts outsiders",
    riskFlags: "Occupational health",
    riskLevel: "Medium",
  },
  {
    id: "rural-6",
    name: "Homeless Patient",
    category: "Rural & Low-Literacy",
    type: "Stress",
    intent: "Emergency Care",
    language: "English (US)",
    emotionalState: "Desperate, Survival-focused",
    behaviorTraits: "No fixed address, Inconsistent contact",
    riskFlags: "Social services needed",
    riskLevel: "High",
  },
  {
    id: "rural-7",
    name: "Native American Reservation",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Tribal Health Services",
    language: "English / Native language",
    emotionalState: "Neutral",
    behaviorTraits: "Cultural health practices",
    riskFlags: "Cultural sensitivity required",
    riskLevel: "Medium",
  },
  {
    id: "rural-8",
    name: "Low-Income Single Parent",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Medicaid Question",
    language: "English (US)",
    emotionalState: "Stressed, Overwhelmed",
    behaviorTraits: "Limited resources, Multiple barriers",
    riskFlags: "Social support needed",
    riskLevel: "Medium",
  },
  {
    id: "rural-9",
    name: "Functionally Illiterate Adult",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "Form Assistance",
    language: "English (US)",
    emotionalState: "Ashamed, Defensive",
    behaviorTraits: "Hides inability to read/write",
    riskFlags: "Requires alternative communication",
    riskLevel: "High",
  },
  {
    id: "rural-10",
    name: "Deaf/Hard of Hearing Rural",
    category: "Rural & Low-Literacy",
    type: "Default",
    intent: "TTY/Relay Service",
    language: "English (Text)",
    emotionalState: "Patient, Accustomed to barriers",
    behaviorTraits: "Uses assistive technology",
    riskFlags: "ADA accommodation required",
    riskLevel: "Medium",
  },

  // Category 9: Corporate & Insurance Personas (10)
  {
    id: "corp-1",
    name: "Corporate Benefits Manager",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "Group Coverage Inquiry",
    language: "English (US)",
    emotionalState: "Professional, Efficient",
    behaviorTraits: "Business-focused",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "corp-2",
    name: "Insurance Claims Adjuster",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "Claim Verification",
    language: "English (US)",
    emotionalState: "Neutral, Investigative",
    behaviorTraits: "Asks detailed questions",
    riskFlags: "Fraud detection protocol",
    riskLevel: "Medium",
  },
  {
    id: "corp-3",
    name: "Prior Authorization Required",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "PA Status Check",
    language: "English (US)",
    emotionalState: "Frustrated with bureaucracy",
    behaviorTraits: "Understands insurance complexity",
    riskFlags: "Treatment delay potential",
    riskLevel: "Medium",
  },
  {
    id: "corp-4",
    name: "Out-of-Network Dispute",
    category: "Corporate & Insurance",
    type: "Stress",
    intent: "Coverage Appeal",
    language: "English (US)",
    emotionalState: "Angry, Financially stressed",
    behaviorTraits: "Disputes charges",
    riskFlags: "Billing conflict",
    riskLevel: "High",
  },
  {
    id: "corp-5",
    name: "Workers' Compensation Claim",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "Work Injury Report",
    language: "English (US)",
    emotionalState: "Formal, Cautious",
    behaviorTraits: "Knows legal implications",
    riskFlags: "Legal documentation needed",
    riskLevel: "Medium",
  },
  {
    id: "corp-6",
    name: "Medicare Advantage Member",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "Plan Benefits Question",
    language: "English (US)",
    emotionalState: "Elderly, Confused by options",
    behaviorTraits: "Needs simple explanations",
    riskFlags: "Complex insurance navigation",
    riskLevel: "Medium",
  },
  {
    id: "corp-7",
    name: "Denied Claim Appeal",
    category: "Corporate & Insurance",
    type: "Stress",
    intent: "Appeal Process",
    language: "English (US)",
    emotionalState: "Frustrated, Determined",
    behaviorTraits: "Prepared with documentation",
    riskFlags: "Escalation likely",
    riskLevel: "High",
  },
  {
    id: "corp-8",
    name: "FSA/HSA Question",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "Account Management",
    language: "English (US)",
    emotionalState: "Neutral, Administrative",
    behaviorTraits: "Financially savvy",
    riskFlags: "None",
    riskLevel: "Low",
  },
  {
    id: "corp-9",
    name: "COBRA Continuation",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "Coverage Extension",
    language: "English (US)",
    emotionalState: "Anxious about job loss",
    behaviorTraits: "Facing financial uncertainty",
    riskFlags: "Coverage gap prevention",
    riskLevel: "Medium",
  },
  {
    id: "corp-10",
    name: "Third-Party Administrator",
    category: "Corporate & Insurance",
    type: "Default",
    intent: "Provider Relations",
    language: "English (US)",
    emotionalState: "Professional, Business-like",
    behaviorTraits: "Insurance industry insider",
    riskFlags: "None",
    riskLevel: "Low",
  },

  // Category 10: Edge / Failure Mode Personas (10)
  {
    id: "edge-1",
    name: "Silent Caller",
    category: "Edge / Failure Mode",
    type: "Stress",
    intent: "Unknown",
    language: "None",
    emotionalState: "Unknown",
    behaviorTraits: "No verbal response",
    riskFlags: "Potential emergency or technical issue",
    riskLevel: "High",
  },
  {
    id: "edge-2",
    name: "Pocket Dial",
    category: "Edge / Failure Mode",
    type: "Default",
    intent: "Accidental Call",
    language: "Background noise",
    emotionalState: "Unaware",
    behaviorTraits: "No intentional communication",
    riskFlags: "Call termination needed",
    riskLevel: "Low",
  },
  {
    id: "edge-3",
    name: "Prank Caller",
    category: "Edge / Failure Mode",
    type: "Stress",
    intent: "Harassment",
    language: "English (US)",
    emotionalState: "Juvenile, Mischievous",
    behaviorTraits: "Makes inappropriate jokes",
    riskFlags: "Abuse of system",
    riskLevel: "Medium",
  },
  {
    id: "edge-4",
    name: "Wrong Number - Persistent",
    category: "Edge / Failure Mode",
    type: "Default",
    intent: "Misdial",
    language: "English (US)",
    emotionalState: "Confused, Insistent",
    behaviorTraits: "Argues they have right number",
    riskFlags: "Communication failure",
    riskLevel: "Low",
  },
  {
    id: "edge-5",
    name: "Extremely Poor Connection",
    category: "Edge / Failure Mode",
    type: "Stress",
    intent: "Various",
    language: "Unintelligible",
    emotionalState: "Frustrated with tech",
    behaviorTraits: "Cuts in and out",
    riskFlags: "Communication breakdown",
    riskLevel: "Medium",
  },
  {
    id: "edge-6",
    name: "Voice Synthesizer User",
    category: "Edge / Failure Mode",
    type: "Default",
    intent: "Accessibility Need",
    language: "Synthesized voice",
    emotionalState: "Patient",
    behaviorTraits: "Uses assistive technology",
    riskFlags: "Non-standard communication",
    riskLevel: "Medium",
  },
  {
    id: "edge-7",
    name: "Severe Stutter",
    category: "Edge / Failure Mode",
    type: "Default",
    intent: "Various",
    language: "English (US)",
    emotionalState: "Self-conscious, Patient",
    behaviorTraits: "Speech impediment",
    riskFlags: "Requires extra time",
    riskLevel: "Medium",
  },
  {
    id: "edge-8",
    name: "Confused AI Test",
    category: "Edge / Failure Mode",
    type: "Stress",
    intent: "System Testing",
    language: "English (US)",
    emotionalState: "Adversarial",
    behaviorTraits: "Tries to break AI",
    riskFlags: "Intentional edge case testing",
    riskLevel: "Medium",
  },
  {
    id: "edge-9",
    name: "Child Calling for Parent",
    category: "Edge / Failure Mode",
    type: "Default",
    intent: "Emergency Help",
    language: "English (US - Child voice)",
    emotionalState: "Scared, Young",
    behaviorTraits: "Limited vocabulary, Parent in crisis",
    riskFlags: "Child safety / parent emergency",
    riskLevel: "High",
  },
  {
    id: "edge-10",
    name: "Non-verbal Communicator",
    category: "Edge / Failure Mode",
    type: "Default",
    intent: "Various",
    language: "Non-verbal sounds",
    emotionalState: "Frustrated",
    behaviorTraits: "Uses sounds, not words",
    riskFlags: "Severe communication barrier",
    riskLevel: "High",
  },
]

// Legacy export for backward compatibility
export const indiaPersonas = defaultPersonas
